import { useState } from 'react';
import { api, useQuery } from '../api.js';
import { List, Row, Tile, type TileColor } from '../controls.js';
import { Drawer, ErrorNote, Pane, Panes, Pill, Spinner, useToast } from '../ui.js';
import { BookIcon, CheckIcon, DocIcon, ListIcon, ShieldIcon, AlertIcon, GraduationIcon } from '../icons.js';

interface Doc {
  id: number; category: string; kind: string; title: string; summary: string;
  version: string; updatedOn: string; requiresAck: boolean; pinned: boolean;
  acknowledged: boolean; acknowledgedAt: number | null; outstanding: boolean;
}
interface Library {
  categories: { name: string; documents: Doc[] }[];
  outstandingCount: number;
  total: number;
}

/** Each kind of document gets its own tile colour and glyph, as in Settings. */
const LOOK: Record<string, { color: TileColor; Glyph: (p: { className?: string }) => JSX.Element }> = {
  policy: { color: 'blue', Glyph: BookIcon },
  checklist: { color: 'green', Glyph: ListIcon },
  form: { color: 'indigo', Glyph: DocIcon },
  safety: { color: 'red', Glyph: ShieldIcon },
  syllabus: { color: 'purple', Glyph: GraduationIcon },
  notice: { color: 'orange', Glyph: AlertIcon },
};

export function PortalDocuments(): JSX.Element {
  const { data, error, reload } = useQuery<Library>('/documents');
  const [openId, setOpenId] = useState<number | null>(null);

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  if (data.total === 0) {
    return (
      <div className="card">
        <div className="empty">
          <BookIcon style={{ width: 30, height: 30, opacity: 0.4, marginBottom: 10 }} />
          <div style={{ fontWeight: 600, color: 'var(--label-1)' }}>No documents yet</div>
          <div style={{ marginTop: 4, maxWidth: 380, marginInline: 'auto', textWrap: 'pretty' }}>
            Your school has not published its operations manual, checklists or forms here yet.
            Anything they add — and anything they ask you to sign — appears on this shelf.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 22 }}>
      {data.outstandingCount > 0 && (
        <div className="card" style={{ borderLeft: `3px solid var(--sys-orange)` }}>
          <div className="card__body row" style={{ gap: 12, justifyContent: 'space-between' }}>
            <span className="row" style={{ gap: 12, flex: 1, minWidth: 200, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
              <Tile color="orange"><AlertIcon /></Tile>
              <span>
                <strong style={{ display: 'block' }}>
                  {data.outstandingCount} document{data.outstandingCount === 1 ? '' : 's'} need your signature
                </strong>
                <span className="small muted">
                  Your instructor sees these too. Sign them before your next flight.
                </span>
              </span>
            </span>
          </div>
        </div>
      )}

      {data.categories.map((group) => (
        <List key={group.name} header={group.name}>
          {group.documents.map((doc) => {
            const look = LOOK[doc.kind] ?? LOOK.policy;
            return (
              <Row
                key={doc.id}
                tile={<Tile color={look.color}><look.Glyph /></Tile>}
                title={doc.title}
                sub={doc.summary}
                onClick={() => setOpenId(doc.id)}
                chevron
                trailing={
                  doc.outstanding ? <Pill tone="amber">Sign</Pill>
                  : doc.acknowledged ? <Pill tone="green">Signed</Pill>
                  : doc.pinned ? <Pill tone="blue">Pinned</Pill>
                  : undefined
                }
              />
            );
          })}
        </List>
      ))}

      <p className="note" style={{ marginTop: 0 }}>
        {data.total} documents. Versions are what the school has published; a signature applies to the
        version it was given against, so a revised document asks for it again.
      </p>

      {openId && <DocumentSheet id={openId} onClose={() => setOpenId(null)} onChanged={reload} />}
    </div>
  );
}

function DocumentSheet({ id, onClose, onChanged }: {
  id: number; onClose: () => void; onChanged: () => void;
}): JSX.Element {
  const { data, reload } = useQuery<Doc & { body: string }>(`/documents/${id}`);
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const sign = async () => {
    setBusy(true);
    try {
      await api.post(`/documents/${id}/acknowledge`);
      toast.ok('Signed', `${data?.title} v${data?.version} recorded against your account.`);
      reload();
      onChanged();
    } catch (err) {
      toast.error('Could not record that', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const look = data ? LOOK[data.kind] ?? LOOK.policy : LOOK.policy;

  return (
    <Drawer
      title={data?.title ?? 'Document'}
      subtitle={data ? `Version ${data.version} · updated ${data.updatedOn}` : undefined}
      onClose={onClose}
      footer={data?.requiresAck && (
        data.acknowledged
          ? <span className="row" style={{ gap: 7, color: 'var(--sys-green)', fontWeight: 600, fontSize: 13 }}>
              <CheckIcon style={{ width: 16, height: 16 }} />
              Signed{data.acknowledgedAt ? ` ${new Date(data.acknowledgedAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
            </span>
          : <button className="btn btn--primary btn--block" disabled={busy} onClick={sign}>
              I have read and understood this
            </button>
      )}
    >
      {!data && <Spinner />}
      {data && (
        <Panes>
          <Pane title="About">
            <div className="row" style={{ gap: 12, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
              <Tile color={look.color} size="lg"><look.Glyph /></Tile>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 600 }}>{data.category}</span>
                <span className="small muted" style={{ display: 'block', textWrap: 'pretty' }}>{data.summary}</span>
              </span>
            </div>
            <div className="stack" style={{ gap: 6, marginTop: 14, fontSize: 12.5 }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="muted">Version</span><strong>{data.version}</strong>
              </div>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="muted">Updated</span><strong>{data.updatedOn}</strong>
              </div>
              {data.requiresAck && (
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="muted">Signature</span>
                  <Pill tone={data.acknowledged ? 'green' : 'amber'}>
                    {data.acknowledged ? 'Signed' : 'Required'}
                  </Pill>
                </div>
              )}
            </div>
            {data.outstanding && (
              <div style={{
                marginTop: 14, padding: '10px 12px', borderRadius: 'var(--r-md)',
                background: 'var(--tint-orange)', color: 'var(--k-pending-fg)', fontSize: 12.5,
                textWrap: 'pretty',
              }}>
                This version has not been signed. Read it through and sign at the bottom.
              </div>
            )}
          </Pane>

          <Pane title="Document">
            <div style={{
              fontSize: 13.5, lineHeight: 1.65, whiteSpace: 'pre-wrap',
              textWrap: 'pretty', color: 'var(--label-1)', maxWidth: '68ch',
            }}>
              {data.body}
            </div>
          </Pane>
        </Panes>
      )}
    </Drawer>
  );
}
